// client/src/utils/api/admins.js

import { useCallback } from 'react';
import { useAuth } from '@/components/hooks/useAuth';
import { api, handleRequestError } from './config';
import { getLogger } from "@/utils/logger";

const log = getLogger("admins");


// ============================================================================
// ADMIN MANAGEMENT - BASIC CRUD OPERATIONS
// ============================================================================

/**
 * Get all admins
 * @returns {Promise<Array>} List of all admins
 */
export const getAllAdmins = async () => {
  try {
    const response = await api.get('/admin/all');
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to fetch admins");
  }
};

/**
 * Get admin by ID
 * @param {string} id - Admin ID
 * @returns {Promise<Object>} Admin data
 */
export const getAdminById = async (id) => {
  try {
    const response = await api.get(`/admin/${id}`);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to fetch admin");
  }
};

/**
 * Update admin information
 * @param {string} id - Admin ID
 * @param {Object} adminData - Updated admin data
 * @returns {Promise<Object>} Updated admin
 */
export const updateAdmin = async (id, adminData) => {
  try {
    const response = await api.put(`/admin/update/${id}`, adminData);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to update admin");
  }
};

/**
 * Delete admin
 * @param {string} id - Admin ID
 * @returns {Promise<Object>} Response data
 */
export const deleteAdmin = async (id) => {
  try {
    const response = await api.delete(`/admin/delete/${id}`);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to delete admin");
  }
};

/**
 * Update admin status (enable/disable) - Boolean version
 * @param {string} id - Admin ID
 * @param {boolean} isActive - Active status
 * @returns {Promise<Object>} Updated admin
 */
export const updateAdminStatus = async (id, isActive) => {
  try {
    const response = await api.put(`/admin/${id}/status`, { isActive });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to update admin status");
  }
};

/**
 * Update admin status - String version (alternative endpoint)
 * Use this when your backend expects a status string instead of boolean
 * @param {string} id - Admin ID
 * @param {string} status - Status string (e.g., "active", "inactive", "suspended")
 * @returns {Promise<Object>} Updated admin
 */
export const updateAdminStatusString = async (id, status) => {
  try {
    const response = await api.put(`/admin/${id}/status`, { status });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to update admin status");
  }
};

// ============================================================================
// ADMIN PROPERTY PROFILES
// ============================================================================

/**
 * Get admin's allowed property profiles (requires authentication via interceptor)
 * This function uses the axios interceptor to automatically add the Auth0 token
 * @returns {Promise<Array>} List of allowed profiles
 */
export const getAdminPropertyProfiles = async () => {
  try {
    const response = await api.get('/admin/property-profiles');
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to fetch admin property profiles");
  }
};

/**
 * Update admin's allowed profiles (Version 1 - with adminId)
 * @param {string} adminId - Admin ID
 * @param {Array<string>} profileIds - Array of profile IDs
 * @returns {Promise<Object>} Updated admin data
 */
export const updateAdminProfilesById = async (adminId, profileIds) => {
  try {
    const response = await api.put(`/admin/${adminId}/profiles`, { 
      allowedProfiles: profileIds 
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to update admin profiles");
  }
};

/**
 * Update current admin's property profiles (Version 2 - for current admin)
 * Use this when updating the currently authenticated admin's profiles
 * @param {Array<string>} profiles - Array of profile IDs
 * @returns {Promise<Object>} Updated admin data
 */
export const updateAdminProfiles = async (profiles) => {
  try {
    const response = await api.put('/admin/profiles', { profiles });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to update admin profiles");
  }
};

// ============================================================================
// AUTHENTICATED ADMIN PROFILE API HOOK
// ============================================================================

/**
 * Custom hook for authenticated admin profile operations
 * Uses app-level auth provider contract and shared API interceptor authentication
 * 
 * This hook enforces authenticated usage and relies on the centralized
 * provider-backed token lifecycle for request authorization.
 * 
 * @returns {Object} Profile API methods
 */
export function useAdminProfileApi() {
  const { isAuthenticated, isAdmin, isAgent, userRoles = [], userPermissions = [] } = useAuth();
  const hasSystemAccess = Boolean(
    isAdmin ||
    isAgent ||
    (Array.isArray(userRoles) && userRoles.length > 0) ||
    (Array.isArray(userPermissions) && userPermissions.length > 0)
  );

  const assertAuthenticated = useCallback(() => {
    if (isAuthenticated) return;
    const error = new Error('Admin is not authenticated');
    log.error(
      "[admins:useAdminProfileApi] > [Request]: assertAuthenticated > [Response]: error=admin not authenticated",
      error
    );
    throw error;
  }, [isAuthenticated]);

  const assertSystemAccess = useCallback(() => {
    if (hasSystemAccess) return;
    const error = new Error('System access required');
    log.info(
      "[admins:useAdminProfileApi] > [Request]: assertSystemAccess > [Response]: skipped > [Comment]: non-system identity"
    );
    throw error;
  }, [hasSystemAccess]);
  
  /**
   * Get current admin's profile
   * @returns {Promise<Object>} Admin profile data
   */
  const getAdminProfile = useCallback(async () => {
    try {
      assertAuthenticated();
      if (!hasSystemAccess) {
        log.info(
          "[admins:useAdminProfileApi] > [Request]: GET /admin/profile > [Response]: skipped > [Comment]: non-system identity"
        );
        return null;
      }
      const response = await api.get('/admin/profile');
      return response.data;
    } catch (error) {
      log.error(
        `[admins:useAdminProfileApi] > [Request]: GET /admin/profile > [Response]: error=${error?.message || "unknown"}`
      );
      if (error.response?.status === 401) {
        log.info(
          "[admins:useAdminProfileApi] > [Request]: GET /admin/profile > [Response]: unauthorized"
        );
      }
      throw error;
    }
  }, [assertAuthenticated, hasSystemAccess]);
  
  /**
   * Update current admin's profile (JSON data only)
   * @param {Object} profileData - Updated profile data
   * @returns {Promise<Object>} Updated admin profile
   */
  const updateAdminProfile = useCallback(async (profileData) => {
    try {
      assertAuthenticated();
      assertSystemAccess();
      const response = await api.put('/admin/profile', profileData);
      return response.data;
    } catch (error) {
      log.error(
        `[admins:useAdminProfileApi] > [Request]: PUT /admin/profile > [Response]: error=${error?.message || "unknown"}`
      );
      if (error.response?.status === 401) {
        log.info(
          "[admins:useAdminProfileApi] > [Request]: PUT /admin/profile > [Response]: unauthorized"
        );
      }
      throw error;
    }
  }, [assertAuthenticated, assertSystemAccess]);

  /**
   * Update current admin's profile with file upload (avatar/images)
   * @param {FormData} formData - FormData with profile data and files
   * @returns {Promise<Object>} Updated admin profile
   */
  const updateAdminProfileWithFiles = useCallback(async (formData) => {
    try {
      assertAuthenticated();
      assertSystemAccess();
      const response = await api.put('/admin/profile', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response.data;
    } catch (error) {
      log.error(
        `[admins:useAdminProfileApi] > [Request]: PUT /admin/profile (multipart) > [Response]: error=${error?.message || "unknown"}`
      );
      if (error.response?.status === 401) {
        log.info(
          "[admins:useAdminProfileApi] > [Request]: PUT /admin/profile (multipart) > [Response]: unauthorized"
        );
      }
      throw error;
    }
  }, [assertAuthenticated, assertSystemAccess]);

  /**
   * Get current admin's property profiles
   * @returns {Promise<Array>} List of admin's property profiles
   */
  const getAdminPropertyProfiles = useCallback(async () => {
    try {
      assertAuthenticated();
      assertSystemAccess();
      const response = await api.get('/admin/property-profiles');
      return response.data;
    } catch (error) {
      log.error(
        `[admins:useAdminProfileApi] > [Request]: GET /admin/property-profiles > [Response]: error=${error?.message || "unknown"}`
      );
      if (error.response?.status === 401) {
        log.info(
          "[admins:useAdminProfileApi] > [Request]: GET /admin/property-profiles > [Response]: unauthorized"
        );
      }
      throw error;
    }
  }, [assertAuthenticated, assertSystemAccess]);
  
  return {
    getAdminProfile,
    updateAdminProfile,
    updateAdminProfileWithFiles,
    getAdminPropertyProfiles
  };
}

/**
 * Get properties associated with an admin profile
 * @param {string} adminId - Admin ID
 * @returns {Promise<Array>} List of properties using this admin's profile
 */
export const getAdminProperties = async (adminId) => {
  try {
    const response = await api.get(`/admin/${adminId}/properties`);
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to fetch admin properties");
  }
};


/**
 * Reassign properties from one admin profile to another
 * Used when disabling an admin who has properties associated with their profile
 * @param {string} adminId - Current admin ID whose properties need reassignment
 * @param {string} newProfileId - New profile ID to assign properties to
 * @returns {Promise<Object>} Reassignment result
 */
export const reassignAdminProperties = async (adminId, newProfileId) => {
  try {
    const response = await api.put(`/admin/${adminId}/reassign-properties`, {
      newProfileId
    });
    return response.data;
  } catch (error) {
    handleRequestError(error, "Failed to reassign admin properties");
  }
};

/**
 * Get public profile information for an admin
 * Used for displaying contact information without authentication
 * @param {string} profileId - Profile ID
 * @returns {Promise<Object|null>} Public profile data or null if not found
 */
export const getAdminPublicProfile = async (profileId) => {
  try {
    const response = await api.get(`/admin/public-profile/${profileId}`);
    return response.data;
  } catch (error) {
    log.error(
      `[admins:getAdminPublicProfile] > [Request]: GET /admin/public-profile/${profileId} > [Response]: error=${error?.message || "unknown"}`
    );
    return null;
  }
};

/**
 * Get count of properties associated with an admin profile
 * Useful for checking before disabling an admin
 * @param {string} adminId - Admin ID
 * @returns {Promise<number>} Count of properties using this admin's profile
 */
export const getAdminPropertiesCount = async (adminId) => {
  try {
    const response = await api.get(`/admin/${adminId}/properties-count`);
    return response.data.count || 0;
  } catch (error) {
    log.error(
      `[admins:getAdminPropertiesCount] > [Request]: GET /admin/${adminId}/properties-count > [Response]: error=${error?.message || "unknown"}`
    );
    return 0; // Return 0 on error to allow proceeding safely
  }
};
